import React, { useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { AvatarLarge, AvatarMedium, IconSuccess } from '../../components';
import Flag from 'react-world-flags';

import css from './ListingPage.module.css';

const getDocumentTypeLabel = (type, intl) => {
  const types = {
    professional_certificate: intl.formatMessage({
      id: 'AddActivityForm.docTypeProfessionalCert',
      defaultMessage: 'Professional Certificate',
    }),
    association_registration: intl.formatMessage({
      id: 'AddActivityForm.docTypeAssociationReg',
      defaultMessage: 'Association Registration',
    }),
    safety_certificate: intl.formatMessage({
      id: 'AddActivityForm.docTypeSafetyCert',
      defaultMessage: 'Safety Certificate',
    }),
    insurance_coverage: intl.formatMessage({
      id: 'AddActivityForm.docTypeInsuranceCoverage',
      defaultMessage: 'Insurance Coverage',
    }),
    chamber_of_commerce: intl.formatMessage({
      id: 'AddActivityForm.docTypeChamberCommerce',
      defaultMessage: 'Chamber of Commerce',
    }),
    business_license: intl.formatMessage({
      id: 'AddActivityForm.docTypeBusinessLicense',
      defaultMessage: 'Business License',
    }),
  };

  return types[type] || intl.formatMessage({ id: 'General.document', defaultMessage: 'Document' });
};

const SectionAvatar = props => {
  const { user } = props;
  const intl = useIntl();

  const { displayName, publicData = {}, metadata = {} } = user?.attributes?.profile || {};
  const { verification, activities = {} } = publicData;
  const verificationAccess = verification?.access || {};
  const certificatesEvidence = verification?.evidence || [];
  
  // Get member since year
  const memberSince = user?.attributes?.createdAt 
    ? new Date(user.attributes.createdAt).getFullYear() 
    : null;

  // Get verified disciplines (limit to top 3)
  const verifiedDisciplines = useMemo(() => {
    const allDisciplines = [];
    Object.entries(activities).forEach(([activity, disciplines]) => {
      if (Array.isArray(disciplines)) {
        disciplines.forEach(item => {
          const disciplineKey = item?.discipline;
          if (disciplineKey && verificationAccess[disciplineKey] === true) {
            allDisciplines.push(disciplineKey);
          }
        });
      }
    });
    // Remove duplicates and limit to 3
    return [...new Set(allDisciplines)].slice(0, 3);
  }, [activities, verificationAccess]);

  // Get verified certificates
  const verifiedCertificates = useMemo(() => {
    if (!verification?.documentStatusHistory) return [];
    
    const statusHistory = verification.documentStatusHistory;
    
    const approvedCerts = certificatesEvidence.filter(cert => {
      let certStatus = null;
      const id = cert.id || '';
      
      // 1. Try match with ID stripped of extension (New Backend Format)
      const lastDotIndex = id.lastIndexOf('.');
      const idNoExt = lastDotIndex > 0 ? id.substring(0, lastDotIndex) : id;
      certStatus = statusHistory[idNoExt];

      // 2. Try direct ID match (Legacy plain)
      if (!certStatus) {
        certStatus = statusHistory[id];
      }
      
      // 3. Try encoded ID match (Legacy encoded)
      if (!certStatus && id) {
        const encodedId = id.replace(/\./g, '%2E');
        certStatus = statusHistory[encodedId];
      }
      
      if (!certStatus && cert.fileName) {
        certStatus = statusHistory[cert.fileName];
      }
      
      return certStatus?.status === 'approved';
    }).map(cert => ({
      id: cert.id,
      type: cert.type,
      label: getDocumentTypeLabel(cert.type, intl),
      country: cert.country,
      issuer: cert.issuer,
    }));
    
    return approvedCerts;
  }, [certificatesEvidence, verification, intl]);

  // Check if provider has any verification
  const hasVerification = verifiedDisciplines.length > 0 || verifiedCertificates.length > 0;

  return (
    <div className={css.sectionAvatarWrapper}>
      {/* Top section: Avatar + Name + Disciplines */}
      <div className={css.sectionAvatar}>
        <div className={css.avatarContainer}>
          <AvatarLarge user={user} className={css.avatarDesktop} />
          <AvatarMedium user={user} className={css.avatarMobile} />
        </div>
        
        <div className={css.userInfo}>
          <span className={css.userName}>
            <FormattedMessage id="ListingPage.offeredBy" values={{ name: displayName }} />
          </span>
          
          {/* Verified disciplines - right next to name */}
          {verifiedDisciplines.length > 0 && (
            <div className={css.verifiedDisciplines}>
              {verifiedDisciplines.map(discipline => (
                <span key={discipline} className={css.verifiedDisciplineChip}>
                  <IconSuccess className={css.disciplineCheckIcon} />
                  <FormattedMessage id={`General.${discipline}`} />
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
      
      {/* Bottom section: Verification message and certificates */}
      {hasVerification && (
        <div className={css.providerCredentials}>
          {/* Trust message */}
          <p className={css.verificationMessage}>
            <FormattedMessage 
              id="ListingPage.providerVerificationMessage"
              defaultMessage="This provider has been verified based on submitted documents and qualifications."
            />
          </p>
          
          {/* Certificates section */}
          {verifiedCertificates.length > 0 && (
            <div className={css.certificatesSection}>
              <h4 className={css.certificatesTitle}>
                <FormattedMessage id="ListingPage.certificates" defaultMessage="Certificates" />
              </h4>
              
              <div className={css.certificatesBox}>
                {verifiedCertificates.map(cert => (
                  <div key={cert.id} className={css.certificateChip}>
                    <IconSuccess className={css.certCheckIcon} />
                    <span className={css.certLabel}>{cert.label}</span>
                    {cert.issuer && (
                      <span className={css.certIssuer}>{cert.issuer}</span>
                    )}
                    {cert.country && (
                      <span className={css.certCountryFlag}>
                        <Flag code={cert.country} />
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Member since */}
          {memberSince && (
            <div className={css.memberSinceRow}>
              <span className={css.memberSinceBadge}>
                <FormattedMessage 
                  id="ListingPage.memberSince" 
                  values={{ year: memberSince }}
                />
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SectionAvatar;
